// searchSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api/axios';

/* A refusal is a real HTTP status carrying { code, message } — there is no
   status field in the body to test. The text is what the screens render, so
   the code is only the fallback when a route answers without a sentence. */
const apiError = (err, fallback) =>
  err.response?.data?.message || err.response?.data?.code || fallback;

/* GET /admin/catalog/search/products answers { success, data, pagination }.
   The inner `data` is the payload's own key, not a leftover envelope. Rows
   carry `fromPaise` — the cheapest live pack, in PAISE. */
export const searchProducts = createAsyncThunk(
  'search/searchProducts',
  async ({ searchTerm, category, limit = 10 }, { rejectWithValue }) => {
    try {
      const params = {
        search: searchTerm,
        category: category,
        limit: limit,
        status: true
      };

      const response = await api.get(`/admin/catalog/search/products`, { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(apiError(error, 'Failed to search products'));
    }
  }
);

/* { success, suggestions: [{ id, name, category, subCategory }] } — fewer
   than two characters answers an empty list rather than scanning. */
export const getSearchSuggestions = createAsyncThunk(
  'search/getSearchSuggestions',
  async (term, { rejectWithValue }) => {
    try {
      const response = await api.get(`/admin/catalog/search/suggestions`, {
        params: { term }
      });
      return response.data.suggestions;
    } catch (error) {
      return rejectWithValue(apiError(error, 'Failed to fetch suggestions'));
    }
  }
);

/* There is no separate best-selling route: it is the product search with its
   own bestSelling filter, which is the flag the toggle writes. */
export const getBestSellingProducts = createAsyncThunk(
  'search/getBestSellingProducts',
  async (limit = 10, { rejectWithValue }) => {
    try {
      const response = await api.get(`/admin/catalog/search/products`, {
        params: { limit, bestSelling: true, status: true }
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(apiError(error, 'Failed to fetch best selling products'));
    }
  }
);

/* Likewise, a category listing is the same search narrowed by category. */
export const getProductsByCategory = createAsyncThunk(
  'search/getProductsByCategory',
  async ({ categoryId, limit = 10 }, { rejectWithValue }) => {
    try {
      const response = await api.get(`/admin/catalog/search/products`, {
        params: { category: categoryId, limit, status: true }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(apiError(error, 'Failed to fetch products'));
    }
  }
);

// Initial state
const initialState = {
  // Categories
  categories: [],
  categoriesLoading: false,
  categoriesError: null,

  // Products
  products: [],
  productsLoading: false,
  productsError: null,

  // Search filters
  searchTerm: '',
  selectedCategory: '',
  
  // Pagination/Results
  totalResults: 0,

  // Suggestions
  suggestions: [],
  suggestionsLoading: false,

  // Best Selling
  bestSellingProducts: [],
  bestSellingLoading: false,
};

// Slice
const searchSlice = createSlice({
  name: 'search',
  initialState,
  reducers: {
    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload;
    },
    setSelectedCategory: (state, action) => {
      state.selectedCategory = action.payload;
    },
    clearSearch: (state) => {
      state.searchTerm = '';
    },
    clearCategory: (state) => {
      state.selectedCategory = '';
    },
    clearFilters: (state) => {
      state.searchTerm = '';
      state.selectedCategory = '';
    },
    resetSearchState: (state) => {
      return initialState;
    }
  },
  extraReducers: (builder) => {
 
    // Search Products
    builder
      .addCase(searchProducts.pending, (state) => {
        state.productsLoading = true;
        state.productsError = null;
      })
      .addCase(searchProducts.fulfilled, (state, action) => {
        state.productsLoading = false;
        state.products = action.payload.data;
        state.totalResults = action.payload.pagination.total;
      })
      .addCase(searchProducts.rejected, (state, action) => {
        state.productsLoading = false;
        state.productsError = action.payload;
        state.products = [];
        state.totalResults = 0;
      });

    // Get Search Suggestions
    builder
      .addCase(getSearchSuggestions.pending, (state) => {
        state.suggestionsLoading = true;
      })
      .addCase(getSearchSuggestions.fulfilled, (state, action) => {
        state.suggestionsLoading = false;
        state.suggestions = action.payload;
      })
      .addCase(getSearchSuggestions.rejected, (state) => {
        state.suggestionsLoading = false;
        state.suggestions = [];
      });

    // Get Best Selling Products
    builder
      .addCase(getBestSellingProducts.pending, (state) => {
        state.bestSellingLoading = true;
      })
      .addCase(getBestSellingProducts.fulfilled, (state, action) => {
        state.bestSellingLoading = false;
        state.bestSellingProducts = action.payload;
      })
      .addCase(getBestSellingProducts.rejected, (state) => {
        state.bestSellingLoading = false;
        state.bestSellingProducts = [];
      });

    // Get Products By Category
    builder
      .addCase(getProductsByCategory.pending, (state) => {
        state.productsLoading = true;
        state.productsError = null;
      })
      .addCase(getProductsByCategory.fulfilled, (state, action) => {
        state.productsLoading = false;
        state.products = action.payload.data;
        state.totalResults = action.payload.pagination.total;
      })
      .addCase(getProductsByCategory.rejected, (state, action) => {
        state.productsLoading = false;
        state.productsError = action.payload;
        state.products = [];
        state.totalResults = 0;
      });
  }
});

// Export actions
export const {
  setSearchTerm,
  setSelectedCategory,
  clearSearch,
  clearCategory,
  clearFilters,
  resetSearchState
} = searchSlice.actions;

// Selectors
export const selectCategories = (state) => state.search.categories;
export const selectCategoriesLoading = (state) => state.search.categoriesLoading;
export const selectProducts = (state) => state.search.products;
export const selectProductsLoading = (state) => state.search.productsLoading;
export const selectSearchTerm = (state) => state.search.searchTerm;
export const selectSelectedCategory = (state) => state.search.selectedCategory;
export const selectTotalResults = (state) => state.search.totalResults;
export const selectSuggestions = (state) => state.search.suggestions;
export const selectBestSellingProducts = (state) => state.search.bestSellingProducts;

// Export reducer
export default searchSlice.reducer;